import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CareerIcon } from 'atoms';
import { ICareerCvCheck } from 'core';
import type { CvSectionId } from 'organisms/CareerProfileForm';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

interface ICvCompleteness {
  checks: ICareerCvCheck[];
  onJump: (section: CvSectionId) => void;
}

/** Проверка резюме → раздел редактора, где она закрывается. */
export const CHECK_SECTION: Record<string, CvSectionId> = {
  has_name: 'cv-basics',
  has_contact: 'cv-basics',
  has_position: 'cv-basics',
  has_skills: 'cv-skills',
  has_experience_or_project: 'cv-projects',
  has_education: 'cv-education',
  has_about: 'cv-about',
};

/**
 * Одна строка заполненности вместо трёх индикаторов (0072, шаг 2).
 *
 * Аудит: над редактором стояли портрет с «Ankieta wypełniona 100%», чек-лист
 * «Wypełniono 7 z 7» и полоса «Kompletność profilu 100%» — три вида одного
 * серверного расчёта. Осталось одно: «Uzupełniono 7 z 7 sekcji». Если чего-то
 * не хватает — раскрытие со ссылками прямо на нужный раздел.
 *
 * 🔴 Это заполненность, а не качество CV и не готовность к работе — так и
 * называется. Проверки приходят С СЕРВЕРА тем же словарём, что этап «CV» на
 * главной: посчитать их здесь значило бы завести второе определение.
 */
export const CvCompleteness = memo(({ checks, onJump }: ICvCompleteness) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!checks.length) return null;

  const done = checks.filter((row) => row.isDone).length;
  const missing = checks.filter((row) => !row.isDone);

  return (
    <Wrapper>
      <Line>
        <Mark $done={!missing.length} aria-hidden>
          <CareerIcon name={missing.length ? 'list' : 'check'} size={18} />
        </Mark>
        <span aria-live="polite">
          {t('careerProfile.completeness.line', { done, total: checks.length })}
        </span>
        {missing.length ? (
          <Toggle
            type="button"
            aria-expanded={open}
            aria-controls="cv-completeness-missing"
            onClick={() => setOpen((value) => !value)}>
            {t('careerProfile.completeness.missing', { n: missing.length })}
            <Chevron $open={open} aria-hidden>
              <CareerIcon name="chevronDown" size={16} />
            </Chevron>
          </Toggle>
        ) : null}
      </Line>
      {missing.length ? (
        <Missing id="cv-completeness-missing" hidden={!open}>
          {missing.map((row) => (
            <li key={row.key}>
              <Jump type="button" onClick={() => onJump(CHECK_SECTION[row.key] ?? 'cv-basics')}>
                {t(`careerProfile.checklist.${row.key}`)}
                <CareerIcon name="arrowRight" size={16} />
              </Jump>
            </li>
          ))}
        </Missing>
      ) : null}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  color: ${careerColor.text};
  ${careerType.body()};
`;

const Mark = styled.span<{ $done: boolean }>`
  display: inline-flex;
  color: ${({ $done }) => ($done ? careerColor.done : careerColor.text2)};
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${careerColor.text2};
  text-decoration: underline;
  cursor: pointer;
  ${careerType.body()};
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Missing = styled.ul`
  margin: 0;
  padding: 0 0 0 30px;
  list-style: none;
`;

const Jump = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.body()};

  &:hover {
    text-decoration: underline;
  }
`;
